"use client";

import { clsx } from "clsx";
import { TabList as RACTabList, Tab as RACTab, TabListStateContext } from "react-aria-components";
import * as m from "motion/react-m";
import { pxToRem } from "@ui/lib/utils";
import { type AriaAttributes, useContext, useEffect, useRef, useState } from "react";
import {
  PILL_SIZES,
  PILL_RING_CLASS,
  PILL_COLORS,
  getPillHoverInset,
  getPillItemPadding,
  type PillSize,
  type PillSurface,
  type PillInactiveTextColor,
} from "./pill-switcher-styles";
import { BaseLink } from "./Link";
import { mergeProps, useHover } from "react-aria";

/** Scroll the container to center the element horizontally */
function scrollToCenter(element: HTMLElement, container: HTMLElement) {
  const elementRect = element.getBoundingClientRect();
  const containerRect = container.getBoundingClientRect();

  // Calculate position to center the element in the container
  const elementCenter = elementRect.left + elementRect.width / 2;
  const containerCenter = containerRect.left + containerRect.width / 2;
  const scrollOffset = elementCenter - containerCenter;

  container.scrollTo({
    left: container.scrollLeft + scrollOffset,
    behavior: "smooth",
  });
}

/** Link styled identically to a tab item */
function TrailingActionLink({
  trailingAction,
  size,
  surface,
  showRing,
  background,
}: {
  trailingAction: { title: string; href: string; icon?: React.ReactNode; target?: string };
  size: PillSize;
  surface: PillSurface;
  showRing: boolean;
  background: boolean;
}) {
  const [isHovered, setIsHovered] = useState(false);
  const { heightClass, padding, textClass } = PILL_SIZES[size];
  const hoverInset = getPillHoverInset(showRing);

  return (
    <BaseLink
      href={trailingAction.href}
      target={trailingAction.target}
      className={clsx(
        "tw-isolate tw-relative tw-flex tw-items-center tw-justify-center tw-cursor-pointer tw-outline-none focus-visible:tw-outline-focus tw-rounded-full tw-no-underline",
        heightClass,
        showRing && PILL_RING_CLASS,
        background && [
          surface === "white" && "tw-bg-white",
          surface === "paper" && "tw-bg-paper",
          surface === "cream" && "tw-bg-cream",
        ],
      )}
      style={{
        paddingLeft: pxToRem(padding),
        paddingRight: pxToRem(padding),
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <m.div
        className='tw-relative tw-flex tw-items-center tw-gap-1.5'
        initial={false}
        animate={{ color: isHovered ? PILL_COLORS.active : PILL_COLORS.inactive.default }}
      >
        <div className={textClass}>{trailingAction.title}</div>
        {trailingAction.icon}
      </m.div>
      <m.div
        className='tw-absolute -tw-z-10 tw-rounded-full'
        style={{
          top: pxToRem(hoverInset),
          bottom: pxToRem(hoverInset),
          left: pxToRem(hoverInset),
          right: pxToRem(hoverInset),
        }}
        initial={false}
        animate={{
          background: isHovered ? PILL_COLORS.hoverBg[surface] : PILL_COLORS.transparentBg[surface],
        }}
      />
    </BaseLink>
  );
}

export type TabListItem = {
  id: string;
} & ({ title: string; shortTitle?: string; icon?: React.ReactNode } | { icon: React.ReactNode; ariaLabel: string });

export type TabListItemWithLink = TabListItem & {
  href: string;
  isSelected?: boolean;
  rel?: string;
};

function getIsLinkList(items: Array<TabListItem | TabListItemWithLink>): items is Array<TabListItemWithLink> {
  return items.every(getIsLinkItem);
}

function getIsLinkItem(item: TabListItem | TabListItemWithLink): item is TabListItemWithLink {
  return "href" in item;
}

function LinkItem({
  children,
  isSelected,
  ...props
}: Omit<React.ComponentPropsWithRef<typeof BaseLink>, "children"> & {
  children: (props: { isSelected: boolean; isHovered: boolean; isDisabled: boolean }) => React.ReactNode;
  isSelected?: boolean;
}) {
  const { isHovered, hoverProps } = useHover({});
  return (
    <BaseLink {...mergeProps(props, hoverProps)}>
      {children({ isHovered, isSelected: isSelected ?? false, isDisabled: false })}
    </BaseLink>
  );
}

function AdaptiveItem({
  item,
  ...props
}: {
  item: TabListItem | TabListItemWithLink;
  className: string;
  style: React.CSSProperties;
  children: (props: { isSelected: boolean; isHovered: boolean; isDisabled: boolean }) => React.ReactNode;
}) {
  const isLinkItem = getIsLinkItem(item);
  if (isLinkItem) {
    return <LinkItem href={item.href} isSelected={item.isSelected} rel={item.rel} {...props} />;
  }
  return <RACTab id={item.id} {...props} />;
}

export function TabListItem({
  item,
  size = "lg",
  surface = "white",
  showRing = true,
  inactiveTextColor = "default",
  isFirst = false,
  isLast = false,
  inheritHeight = false,
  disableSelectionLayout = false,
  style,
  ...ariaProps
}: AriaAttributes & {
  item: TabListItem | TabListItemWithLink;
  size?: PillSize;
  surface?: PillSurface;
  showRing?: boolean;
  inactiveTextColor?: PillInactiveTextColor;
  isFirst?: boolean;
  isLast?: boolean;
  /** When true, skip the explicit height so the item stretches to fill its grid parent */
  inheritHeight?: boolean;
  /**
   * When true, the selection pill skips Motion `layout`. Needed where tabs sit in a CSS grid
   * with `gridColumnStart` — layout projection mis-measures and the pill animation looks wrong.
   */
  disableSelectionLayout?: boolean;
  style?: React.CSSProperties;
}) {
  const { heightClass, widthClass, padding: tabPadding, textClass } = PILL_SIZES[size];
  const hoverInset = getPillHoverInset(showRing);

  return (
    <AdaptiveItem
      item={item}
      className={clsx(
        "tw-isolate tw-relative tw-flex tw-items-center tw-justify-center tw-cursor-pointer tw-outline-none focus-visible:tw-outline-focus tw-rounded-full",
        !inheritHeight && heightClass,
        widthClass,
      )}
      style={{
        ...getPillItemPadding(tabPadding, isFirst, isLast),
        ...style,
      }}
      aria-label={"ariaLabel" in item ? item.ariaLabel : undefined}
      {...ariaProps}
    >
      {({ isSelected, isHovered, isDisabled }) => (
        <>
          {/* Rectangular overlay to prevent gaps between pill-shaped tabs */}
          <div className='tw-absolute tw-inset-0' />

          <m.div
            className='tw-relative tw-flex tw-items-center tw-gap-1.5'
            initial={false}
            animate={{
              color: isSelected || isHovered ? PILL_COLORS.active : PILL_COLORS.inactive[inactiveTextColor],
              opacity: isDisabled ? 0.5 : 1,
            }}
          >
            {item.icon}
            {"title" in item && (
              <div className={clsx(textClass, "shortTitle" in item && "tw-hidden sm:tw-block")}>{item.title}</div>
            )}
            {"shortTitle" in item && <div className={clsx(textClass, "tw-block sm:tw-hidden")}>{item.shortTitle}</div>}
          </m.div>
          <m.div
            layout={!disableSelectionLayout}
            className='tw-absolute -tw-z-10 tw-rounded-full'
            style={
              isSelected
                ? {
                    top: "0rem",
                    bottom: "0rem",
                    left: isFirst ? "0rem" : pxToRem(1),
                    right: isLast ? "0rem" : pxToRem(1),
                  }
                : {
                    top: pxToRem(hoverInset),
                    bottom: pxToRem(hoverInset),
                    left: isFirst ? pxToRem(hoverInset) : pxToRem(hoverInset + 1),
                    right: isLast ? pxToRem(hoverInset) : pxToRem(hoverInset + 1),
                  }
            }
            initial={false}
            animate={{
              background: isSelected
                ? PILL_COLORS.selectedBg
                : isHovered
                  ? PILL_COLORS.hoverBg[surface]
                  : PILL_COLORS.transparentBg[surface],
              boxShadow: isSelected ? PILL_COLORS.selectedShadow : PILL_COLORS.noShadow,
              opacity: isDisabled ? 0.5 : 1,
            }}
          />
        </>
      )}
    </AdaptiveItem>
  );
}

export function TabList({
  items,
  equalWidth = false,
  surface = "white",
  showRing = true,
  size = "lg",
  centered = false,
  fullWidth = false,
  disabled = false,
  trailingAction,
  inactiveTextColor = "default",
  scrollContainerRef: externalScrollRef,
  background = false,
  disableSelectionLayout = false,
  ...props
}: Omit<React.ComponentPropsWithRef<typeof RACTabList>, "items" | "children"> & {
  items: Array<TabListItem> | Array<TabListItemWithLink>;
  equalWidth?: boolean;
  fullWidth?: boolean;
  surface?: PillSurface;
  inactiveTextColor?: PillInactiveTextColor;
  showRing?: boolean;
  size?: PillSize;
  centered?: boolean;
  /** When true, grays out the tab list and disables pointer events */
  disabled?: boolean;
  /** Optional link to render after the tab list, styled identically to a tab */
  trailingAction?: { title: string; href: string; icon?: React.ReactNode; target?: string };
  /** Optional ref to the scroll container - can be used externally for scroll controls */
  scrollContainerRef?: React.RefObject<HTMLDivElement | null>;
  background?: boolean;
  disableSelectionLayout?: boolean;
}) {
  const { heightClass } = PILL_SIZES[size];
  const internalScrollRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = externalScrollRef || internalScrollRef;
  const tabState = useContext(TabListStateContext);
  const isLinkList = getIsLinkList(items);
  const List = isLinkList ? "div" : RACTabList;
  const listProps = isLinkList ? {} : props;

  useEffect(() => {
    if (scrollContainerRef.current && tabState?.selectedKey) {
      const scrollContainer = scrollContainerRef.current;
      // Escape the selectedKey to prevent SyntaxError if it contains special CSS selector characters
      const escapedKey = CSS.escape(String(tabState.selectedKey));
      const element = scrollContainer.querySelector(`[data-key="${escapedKey}"]`);
      if (element instanceof HTMLElement) {
        scrollToCenter(element, scrollContainer);
      }
    }
  }, [tabState?.selectedKey, scrollContainerRef]);

  return (
    <div className={clsx("tw-flow-root", disabled && "tw-opacity-50 tw-pointer-events-none")}>
      <div
        ref={scrollContainerRef}
        className='-tw-mx-full-bleed tw-px-full-bleed tw-overflow-x-auto tw-scrollbar-none tw-py-3 -tw-my-3'
        style={{
          maskImage:
            "linear-gradient(to right, transparent, white var(--full-bleed, 0px), white calc(100% - var(--full-bleed, 0px)), transparent)",
        }}
      >
        <div
          className={clsx(
            "tw-flex tw-items-center tw-gap-1.5",
            fullWidth ? "tw-w-full tw-min-w-max" : ["tw-w-max", !trailingAction && "tw-min-w-full"],
            // some places where this is used passes in a centered prop and some places pass in tw-mx-auto as a className in.
            // This is hacky but done to support the trailingAction
            centered && trailingAction && "tw-mx-auto",
          )}
        >
          <List
            {...listProps}
            className={clsx(
              "tw-grid tw-rounded-full tw-whitespace-nowrap",
              fullWidth ? "tw-w-full tw-min-w-max" : "tw-w-max",
              heightClass,
              showRing && PILL_RING_CLASS,
              equalWidth && "tw-auto-cols-fr",
              centered && !trailingAction && "tw-mx-auto",
              background && [
                surface === "white" && "tw-bg-white",
                surface === "paper" && "tw-bg-paper",
                surface === "cream" && "tw-bg-cream",
              ],
              props.className,
            )}
          >
            {items.map((item, itemIndex) => {
              const isFirst = itemIndex === 0;
              const isLast = itemIndex === items.length - 1;

              return (
                <TabListItem
                  key={item.id}
                  item={item}
                  size={size}
                  surface={surface}
                  showRing={showRing}
                  inactiveTextColor={inactiveTextColor}
                  isFirst={isFirst}
                  isLast={isLast}
                  inheritHeight
                  disableSelectionLayout={disableSelectionLayout}
                  style={{ gridColumnStart: itemIndex + 1 }}
                />
              );
            })}
          </List>
          {trailingAction && (
            <TrailingActionLink
              trailingAction={trailingAction}
              size={size}
              surface={surface}
              background={background}
              showRing={showRing}
            />
          )}
        </div>
      </div>
    </div>
  );
}

// Toggle button variant of TabList - styled to match ButtonToggleGroup
export function TabListToggle({
  items,
  ...props
}: Omit<React.ComponentPropsWithRef<typeof RACTabList>, "items" | "children"> & {
  items: Array<{ id: string; title: string }>;
}) {
  return (
    <div className={clsx("scrollbar-none overflow-x-auto", props.className)}>
      <RACTabList className='inline-flex w-full min-w-fit gap-10 rounded-md border p-2'>
        {items.map(item => (
          <RACTab
            key={item.id}
            id={item.id}
            className={({ isSelected }: { isSelected: boolean }) =>
              clsx(
                "f-ui-04 flex h-32 flex-shrink-0 cursor-pointer items-center justify-center whitespace-nowrap rounded-md p-8 px-12 text-center outline-none transition-colors",
                isSelected ? "bg-inverse text-inverse" : "hover:bg-gray-50",
              )
            }
          >
            {item.title}
          </RACTab>
        ))}
      </RACTabList>
    </div>
  );
}
